import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { useAuth } from './contexts/AuthContext'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import Layout from './components/ModernLayout/ModernLayout'
import GruposPage from './pages/GruposPage'
import EmpresasPage from './pages/EmpresasPage'
import UsuariosPage from './pages/UsuariosPage'
import UsuariosManutencaoPage from './pages/UsuariosManutencaoPage'
import SessoesPage from './pages/SessoesPage'
import LogsPage from './pages/LogsPage'
import AuditLogPage from './pages/AuditLogPage'
import ErrorLogPage from './pages/ErrorLogPage'
import ConfigPage from './pages/ConfigPage'
import ConfigLayout from './pages/ConfigLayout'
import AppearancePage from './pages/AppearancePage'
import BrandingPage from './pages/BrandingPage'
import DicionarioThorPage from './pages/DicionarioThorPage'
import DicionarioDadosPage from './pages/DicionarioDadosPage'
import SistemaGalateaPage from './pages/SistemaGalateaPage'
import AreasFuncionaisPage from './pages/AreasFuncionaisPage'
import BackupsPage from './pages/BackupsPage'
import TelasPage from './pages/TelasPage'
import TelasPageEmpresa from './pages/TelasPageEmpresa'
import MenuThorJavaPage from './pages/MenuThorJavaPage'
import AdministracaoLayout from './pages/AdministracaoLayout'
import AdministracaoPage from './pages/AdministracaoPage'
import PermissoesPage from './pages/PermissoesPage'
import ContextSelectionPage from './pages/ContextSelectionPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import IconShowcasePage from './pages/IconShowcasePage'
import CardsFromMenuPage from './pages/CardsFromMenuPage'
import BasicaPage from './pages/basicas/BasicaPage'
import AppearanceMockPage from './pages/AppearanceMockPage'
import FluxosPage from './pages/FluxosPage'
import ParametrosPage from './pages/ParametrosPage'
import DocumentacaoPage from './pages/DocumentacaoPage'
import RomaneiosPendentesPage from './pages/Logistica/RomaneiosPendentesPage'
import ProtectedRoute from './components/ProtectedRoute'
import { useDynamicRoutes } from './components/DynamicRoutes'
import { NivelAcesso } from './types/auth'

function App() {
  const { isAuthenticated, isLoading } = useAuth()
  const dynamicRoutes = useDynamicRoutes()

  // Se ainda está carregando (e há um token sendo verificado), mostrar loading
  // Mas se não há token, não mostrar loading para evitar "piscar"
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('access_token') : null
  const shouldShowLoading = isLoading && !!token

  if (shouldShowLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          // Só redirecionar se estiver autenticado e não estiver carregando
          isAuthenticated && !isLoading ? <Navigate to="/" replace /> : <LoginPage />
        }
      />
      <Route
        path="/context-selection"
        element={
          // Só acessar se estiver autenticado
          isAuthenticated ? <ContextSelectionPage /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/forgot-password"
        element={
          // Não pode acessar se estiver autenticado
          isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordPage />
        }
      />
      <Route
        path="/reset-password"
        element={
          // Não pode acessar se estiver autenticado
          isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />
        }
      />
      <Route
        path="/icons-showcase"
        element={<IconShowcasePage />}
      />
      <Route path="/mockups" element={<Layout />}>
        <Route path="basicas" element={<CardsFromMenuPage rota="/basicas" />} />
        <Route path="aparencia" element={<AppearanceMockPage />} />
      </Route>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<FluxosPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="fluxos" element={<FluxosPage />} />
        <Route path="basicas">
          <Route index element={<CardsFromMenuPage rota="/basicas" />} />
          <Route path=":slug" element={<BasicaPage />} />
        </Route>
        <Route path="funcionais">
          <Route index element={<CardsFromMenuPage rota="/funcionais" />} />
          <Route path=":slug" element={<BasicaPage />} />
        </Route>
        <Route path="reservadas">
          <Route index element={<CardsFromMenuPage rota="/reservadas" />} />
          <Route path=":slug" element={<BasicaPage />} />
        </Route>
        <Route path="mockups/basicas" element={<CardsFromMenuPage rota="/basicas" />} />
        <Route path="mockups/aparencia" element={<AppearanceMockPage />} />
        <Route path="grupos" element={<GruposPage />} />
        <Route path="grupos/novo" element={<GruposPage />} />
        <Route path="grupos/:id" element={<GruposPage />} />
        <Route path="empresas" element={<EmpresasPage />} />
        <Route path="empresas/novo" element={<EmpresasPage />} />
        <Route path="empresas/:id" element={<EmpresasPage />} />
        <Route
          path="parametros"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <ParametrosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="documentacao/:docType"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <DocumentacaoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="backups"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <BackupsPage />
            </ProtectedRoute>
          }
        />
        <Route path="usuarios/manutencao" element={<UsuariosManutencaoPage />} />
        <Route path="usuarios" element={<UsuariosPage />} />
        <Route path="usuarios/:id" element={<UsuariosPage />} />
        <Route
          path="logistica/romaneios-pendentes"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.OPERADOR}>
              <RomaneiosPendentesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="audit-log"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="log-erros"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <ErrorLogPage />
            </ProtectedRoute>
          }
        />
        <Route path="sessoes" element={<SessoesPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route
          path="sistema-galatea"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <SistemaGalateaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="areas-funcionais"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <AreasFuncionaisPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="config/*"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.GERENTE}>
              <ConfigLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ConfigPage />} />
          <Route
            path="aparencia"
            element={
              <ProtectedRoute requiredLevel={NivelAcesso.GERENTE}>
                <AppearancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="branding"
            element={
              <ProtectedRoute requiredLevel={NivelAcesso.GERENTE}>
                <BrandingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="dicionario-dados"
            element={
              <ProtectedRoute requiredLevel={NivelAcesso.SUPER_ADMIN}>
                <DicionarioDadosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="dicionariothor"
            element={
              <ProtectedRoute requiredLevel={NivelAcesso.SUPER_ADMIN}>
                <DicionarioThorPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="telas"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <TelasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="telas/empresa"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <TelasPageEmpresa />
            </ProtectedRoute>
          }
        />
        <Route
          path="administracao/*"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <AdministracaoLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdministracaoPage />} />
          <Route path="permissoes" element={<PermissoesPage />} />
        </Route>
        <Route
          path="thorjava/menu"
          element={
            <ProtectedRoute requiredLevel={NivelAcesso.ADMINISTRADOR}>
              <MenuThorJavaPage />
            </ProtectedRoute>
          }
        />
        {/* Rotas dinâmicas baseadas em telas do backend */}
        {dynamicRoutes}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
